import { createContext, useContext, useReducer, ReactNode } from "react";

type Action =
  | { type: "OPEN_AUTH_MODAL", authMode?: "auth" | "register" }
  | { type: "CLOSE_AUTH_MODAL" }
  | { type: "OPEN_ONBOARDING_MODAL" }
  | { type: "CLOSE_ONBOARDING_MODAL" }
  | { type: "SET_AUTH_TYPE"; authType: "Google" | "Facebook" | "Mail" | "Booking" | null }
  | { type: "SET_AUTH_MODE"; authMode: "auth" | "register" };

type Dispatch = (action: Action) => void;

type State = {
  authModalOpen: boolean;
  onboardingModalOpen: boolean;
  authType: "Google" | "Facebook" | "Mail" | "Booking" | null;
  authMode: "auth" | "register" | null;
};

const initialState: State = {
  authModalOpen: false,
  onboardingModalOpen: false,
  authType: null,
  authMode: "auth",
};

const AuthContext = createContext<
  { state: State; dispatch: Dispatch } | undefined
>(undefined);

const authReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "OPEN_AUTH_MODAL":
      return { ...state, authModalOpen: true, authMode: action.authMode || "auth" };
    case "CLOSE_AUTH_MODAL":
      return { ...state, authModalOpen: false };
    case "OPEN_ONBOARDING_MODAL":
      return { ...state, onboardingModalOpen: true };
    case "CLOSE_ONBOARDING_MODAL":
      return { ...state, onboardingModalOpen: false };
    case "SET_AUTH_TYPE":
      return { ...state, authType: action.authType };
    case "SET_AUTH_MODE":
      return { ...state, authMode: action.authMode };
    default:
      throw new Error(`Unhandled action type`);
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth должен использоваться внутри AuthProvider");
  }

  const openAuthModal = (authMode?: "auth" | "register", event?: Record<string, unknown>) => {

    if (authMode && authMode !== "auth" && authMode !== "register") {
      authMode = "auth";
    }

    context.dispatch({ type: "OPEN_AUTH_MODAL", authMode });
  };

  const closeAuthModal = () => context.dispatch({ type: "CLOSE_AUTH_MODAL" });

  const openOnboardingModal = () =>
    context.dispatch({ type: "OPEN_ONBOARDING_MODAL" });

  const closeOnboardingModal = () =>
    context.dispatch({ type: "CLOSE_ONBOARDING_MODAL" });

  const setAuthMode = (authMode: "auth" | "register") => {
    context.dispatch({ type: "SET_AUTH_MODE", authMode });
  };

  return {
    ...context,
    openAuthModal,
    closeAuthModal,
    openOnboardingModal,
    closeOnboardingModal,
    setAuthMode,
  };
};
